import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../Components/Footer';
import { getSiteBrandName, setPageTitle } from '../Utils/brand';
import { ShieldIcon, ArrowLeftIcon } from '../Components/Icons';

export default function PrivacyPolicyPage() {
    const brandName = getSiteBrandName();

    useEffect(() => {
        setPageTitle('Privacy Policy');
    }, []);

    return (
        <main className="pt-24 min-h-screen">
            <section className="py-12 px-6 md:px-8 lg:px-16">
                <div className="max-w-4xl mx-auto space-y-10">
                    {/* Header */}
                    <div className="text-center space-y-3">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-neutral-300 text-xs font-medium tracking-wide">
                            <ShieldIcon className="w-3.5 h-3.5 text-sky-400" />
                            <span>Confidentiality & Data Protection</span>
                        </div>
                        <h1 className="text-3xl md:text-5xl font-display font-extrabold text-white tracking-tight">
                            Privacy <span className="bg-gradient-to-r from-sky-400 via-indigo-300 to-purple-400 bg-clip-text text-transparent">Policy</span>
                        </h1>
                        <p className="text-sm md:text-base text-neutral-400 max-w-2xl mx-auto leading-relaxed">
                            Last updated: October 2026. This policy outlines how {brandName} collects, handles, and safeguards your data across our domain marketplace, rental, and DNS forwarding infrastructure.
                        </p>
                    </div>

                    {/* Content Card */}
                    <div className="p-8 md:p-12 space-y-10 text-neutral-300 leading-relaxed border border-white/10 bg-neutral-900/60 backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),0_15px_35px_rgba(0,0,0,0.5)]">
                        
                        {/* Section 1 */}
                        <div className="space-y-3">
                            <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-3">
                                <span className="w-7 h-7 rounded-lg bg-white/[0.06] text-sky-400 flex items-center justify-center text-xs font-semibold border border-white/10">1</span>
                                Information We Collect
                            </h2>
                            <p className="text-xs sm:text-sm text-neutral-400">
                                We practice strict data minimization. Depending on the service you use, we may process:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-xs sm:text-sm text-neutral-400 pl-2">
                                <li><strong className="text-neutral-200">Account & Identity:</strong> Telegram User ID, username, and public profile data when you authenticate via Telegram or interact with our Telegram bot.</li>
                                <li><strong className="text-neutral-200">Technical Configuration:</strong> Target destination URLs (for domain forwarding) and IPv4/IPv6 server A-records (for domain rentals).</li>
                                <li><strong className="text-neutral-200">Transaction Records:</strong> Public blockchain transaction hashes, invoice IDs, payment amounts, and order reference codes.</li>
                                <li><strong className="text-neutral-200">System Logs:</strong> Standard web server access logs (IP address, browser type, timestamp) strictly for security monitoring and DDoS mitigation.</li>
                            </ul>
                        </div>

                        {/* Section 2 */}
                        <div className="space-y-3 pt-6 border-t border-white/[0.06]">
                            <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-3">
                                <span className="w-7 h-7 rounded-lg bg-white/[0.06] text-sky-400 flex items-center justify-center text-xs font-semibold border border-white/10">2</span>
                                How We Use Your Information
                            </h2>
                            <p className="text-xs sm:text-sm text-neutral-400">
                                Your information is utilized solely to provide and maintain high-speed domain services:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-xs sm:text-sm text-neutral-400 pl-2">
                                <li>Automating instant DNS record provisioning (A-records and HTTP 301/302 redirects).</li>
                                <li>Facilitating atomic payment validation and automated order fulfillment.</li>
                                <li>Providing client support, refund processing, and domain management via your personal cabinet.</li>
                                <li>Enforcing our Acceptable Use Policy and preventing malicious activities (phishing, malware, fraud).</li>
                            </ul>
                        </div>

                        {/* Section 3 */}
                        <div className="space-y-3 pt-6 border-t border-white/[0.06]">
                            <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-3">
                                <span className="w-7 h-7 rounded-lg bg-white/[0.06] text-sky-400 flex items-center justify-center text-xs font-semibold border border-white/10">3</span>
                                Payments & Financial Confidentiality
                            </h2>
                            <p className="text-xs sm:text-sm text-neutral-400">
                                We do not store, process, or have access to sensitive financial credentials such as credit card numbers or private cryptographic keys. Payments are processed through decentralized blockchain networks, CryptoBot, or Telegram Stars. All transactions are cryptographically verifiable on-chain.
                            </p>
                        </div>

                        {/* Section 4 */}
                        <div className="space-y-3 pt-6 border-t border-white/[0.06]">
                            <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-3">
                                <span className="w-7 h-7 rounded-lg bg-white/[0.06] text-sky-400 flex items-center justify-center text-xs font-semibold border border-white/10">4</span>
                                Cookies & Local Storage
                            </h2>
                            <p className="text-xs sm:text-sm text-neutral-400">
                                We do not use third-party behavioral advertising cookies. Browser LocalStorage is used exclusively for functional purposes: preserving your active payment step, managing your personal cabinet authentication session, and saving display preferences.
                            </p>
                        </div>

                        {/* Section 5 */}
                        <div className="space-y-3 pt-6 border-t border-white/[0.06]">
                            <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-3">
                                <span className="w-7 h-7 rounded-lg bg-white/[0.06] text-sky-400 flex items-center justify-center text-xs font-semibold border border-white/10">5</span>
                                Third-Party Disclosures
                            </h2>
                            <p className="text-xs sm:text-sm text-neutral-400">
                                We never sell, rent, or monetize user data. Data is shared only with necessary infrastructure providers:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-xs sm:text-sm text-neutral-400 pl-2">
                                <li>DNS infrastructure nodes for updating nameserver routing.</li>
                                <li>Payment processors (CryptoBot, Telegram Bot API) to reconcile transactions.</li>
                                <li>Law enforcement only where legally mandated by authoritative court order with verified jurisdiction.</li>
                            </ul>
                        </div>

                        {/* Section 6 */}
                        <div className="space-y-3 pt-6 border-t border-white/[0.06]">
                            <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-3">
                                <span className="w-7 h-7 rounded-lg bg-white/[0.06] text-sky-400 flex items-center justify-center text-xs font-semibold border border-white/10">6</span>
                                Data Retention & Right to Erasure
                            </h2>
                            <p className="text-xs sm:text-sm text-neutral-400">
                                Expired rental and forwarding configurations are automatically cleared from active routing upon contract conclusion. You may request deletion of your account records at any time by contacting our support team via Telegram or our contact portal.
                            </p>
                        </div>

                        {/* Section 7 */}
                        <div className="space-y-3 pt-6 border-t border-white/[0.06]">
                            <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-3">
                                <span className="w-7 h-7 rounded-lg bg-white/[0.06] text-sky-400 flex items-center justify-center text-xs font-semibold border border-white/10">7</span>
                                Contact & Inquiries
                            </h2>
                            <p className="text-xs sm:text-sm text-neutral-400">
                                If you have questions regarding this Privacy Policy, please reach out via our{' '}
                                <Link to="/contact.html" className="text-sky-400 hover:text-sky-300 underline font-medium">Contact Page</Link>{' '}
                                or review our <Link to="/faq.html" className="text-sky-400 hover:text-sky-300 underline font-medium">Frequently Asked Questions</Link>.
                            </p>
                        </div>
                    </div>

                    {/* Back link */}
                    <div className="text-center pt-2">
                        <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-neutral-300 hover:text-white text-xs sm:text-sm font-medium transition-all">
                            <ArrowLeftIcon className="w-4 h-4" />
                            <span>Return to Marketplace</span>
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}
